import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  MenuBook, 
  QueryStats, 
  Warehouse, 
  AccountBalance, 
  Security 
} from '@mui/icons-material';
import PublicLayout from "../../layouts/PublicLayout";

export default function LargeChain() {
  return (
    <PublicLayout>
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: '#f6521a', fontWeight: 800, letterSpacing: 1.5 }}>
                TPOS FOR ENTERPRISE CHAINS & FRANCHISES
              </Typography>
              
              <Typography variant="h2" sx={{ fontWeight: 600, mt: 1, mb: 2, fontSize: { xs: '1.5rem', md: '2.5rem' }, lineHeight: 1.1 }}>
                Centralize Control. Scale Your Empire. Maintain <span style={{ color: "#f6521a" }}>Brand Consistency.</span>
              </Typography>

              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.5 }}>
                Manage 10 to 1,000+ outlets from one central dashboard with master menu control and global analytics.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.primary', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                Scaling a restaurant brand from one outlet to a national chain requires more than just good food—it requires absolute operational consistency. 
                <strong> TPOS for Large Chains is an enterprise-grade solution designed for "Head Office" control.</strong> Change a price, add a seasonal dish, or update a tax rule at the central hub, and it reflects across every outlet in your network instantly. Compare outlet-wise performance in real-time and manage your supply chain effortlessly.
              </Typography>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Specialized TPOS Features:
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    title: "Master Menu & Centralized Control",
                    desc: "Create 'Master Menus'. Push updates to all stores or specific regions (e.g., North vs. South) with a single click.",
                    icon: <MenuBook sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Comparative Multi-Store Analytics", 
                    desc: "A high-level dashboard to compare 'Store A' vs. 'Store B' on metrics like Average Transaction Value and Footfall.",
                    icon: <QueryStats sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Central Warehouse & Indenting", 
                    desc: "Outlets send 'Indents' to the warehouse. Manage the entire internal supply chain and Inter-Store Transfers (IST).",
                    icon: <Warehouse sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Franchisee Billing & Royalty Tracking", 
                    desc: "Automatically calculate franchisee royalties based on net sales and manage centralized billing for supplies.",
                    icon: <AccountBalance sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Role-Based Access Control (RBAC)", 
                    desc: "Define strict permissions. A Store Manager sees store-level data, while the Owner sees everything across the chain.",
                    icon: <Security sx={{ color: '#f6521a' }} />
                  }
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={5} sx={{ position: 'sticky', top: 100 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden', 
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 550 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'right',
                    float:'right'                    
                  }}
                >
                  <img 
                    src="/img/home-banner.jpg" 
                    alt="Enterprise Chain POS Dashboard" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                
                <Box sx={{ p: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Business Impact</Typography>
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Achieve 100% brand and pricing consistency across your entire geographical footprint." />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Reduce operational overhead by centralizing procurement and accounting functions." />
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </PublicLayout>
  );
}